import {
  Link,
  Outlet,
  createFileRoute,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router';
import { ArchiveX, Inbox, Plus, Search, Send, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { LinkProps } from '@tanstack/react-router';
import type { ComponentType, ReactNode } from 'react';
import { formatUnreadCount } from '~/components/list-panel-states';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { useTRPC } from '~/lib/trpc';

/**
 * Search belongs to the mailbox, not to any folder: one term, one input, and
 * clearing it returns to whichever folder was underneath. Four inputs that each
 * searched everything would differ only in where they sit.
 */
const defaultMailboxSearch = { q: '', includeDiscarded: false };

const mailboxSearchSchema = z.object({
  q: z.string().catch(defaultMailboxSearch.q).default(defaultMailboxSearch.q),
  // Spam and trash are searched only when they were asked for, and the asking
  // lives in the URL so it survives moving between folders.
  includeDiscarded: z
    .boolean()
    .catch(defaultMailboxSearch.includeDiscarded)
    .default(defaultMailboxSearch.includeDiscarded),
});

export const Route = createFileRoute('/_dashboard/mailbox')({
  validateSearch: mailboxSearchSchema,
  search: { middlewares: [stripSearchParams(defaultMailboxSearch)] },
  pendingComponent: MailboxLayoutPending,
  component: MailboxLayoutRoute,
});

const sidebarItemClassName =
  'flex h-10 w-full items-center gap-2 rounded-full border border-transparent bg-background px-3.5 py-2.5 text-sm font-medium leading-5 text-muted-foreground hover:border-border hover:bg-accent hover:text-foreground data-[status=active]:border-border data-[status=active]:bg-accent data-[status=active]:text-foreground';

function MailboxSidebarItemContent({
  children,
  count,
  icon: Icon,
}: {
  children: ReactNode;
  count?: number;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
}) {
  const badge = count && count > 0 ? formatUnreadCount(count) : null;

  return (
    <>
      <span className="flex shrink-0 items-center pr-2">
        <Icon className="size-4 shrink-0" strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {badge && (
        <span className="ml-auto shrink-0 overflow-hidden text-ellipsis text-sm font-medium leading-5 tabular-nums">
          {badge}
        </span>
      )}
    </>
  );
}

// A link is active for its whole subtree by default, so the router's own
// matching covers "on a thread inside Incoming" with no second computation.
function MailboxSidebarLink({
  children,
  count,
  icon: Icon,
  ...props
}: LinkProps & {
  children: ReactNode;
  count?: number;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
}) {
  return (
    <Link
      {...props}
      className={sidebarItemClassName}
      activeOptions={{ includeSearch: false }}
    >
      <MailboxSidebarItemContent icon={Icon} count={count}>
        {children}
      </MailboxSidebarItemContent>
    </Link>
  );
}

function MailboxSearchField() {
  const { t } = useTranslation('mailbox');
  const { q } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <div className="relative w-full">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        strokeWidth={2}
      />
      <Input
        type="search"
        value={q}
        onChange={(event) => {
          const nextQuery = event.currentTarget.value;

          void navigate({
            to: '.',
            search: (previous) => ({ ...previous, q: nextQuery }),
            replace: true,
          });
        }}
        placeholder={t('Search')}
        aria-label={t('Search mail')}
        className="h-10 rounded-full pl-9 shadow-none"
      />
    </div>
  );
}

function MailboxLayoutRoute() {
  const { t } = useTranslation('mailbox');
  const trpc = useTRPC();
  const { data: unreadIncomingCount } = useSuspenseQuery(
    trpc.emails.views.getUnreadIncomingThreadCount.queryOptions(),
  );

  return (
    <>
      <aside className="flex w-64 shrink-0 flex-col items-start self-stretch border-r border-border bg-background">
        <div className="flex flex-col items-start gap-3 self-stretch border-b border-border px-3 py-5">
          <MailboxSidebarLink to="/mailbox/new-message" icon={Plus}>
            {t('New Message')}
          </MailboxSidebarLink>
          <MailboxSearchField />
        </div>
        <nav className="flex flex-col items-start gap-1 self-stretch border-b border-border px-3 py-5">
          <MailboxSidebarLink
            to="/mailbox/incoming"
            icon={Inbox}
            count={unreadIncomingCount}
          >
            {t('Incoming')}
          </MailboxSidebarLink>
          <MailboxSidebarLink to="/mailbox/sent" icon={Send}>
            {t('Sent')}
          </MailboxSidebarLink>
          <MailboxSidebarLink
            to="/mailbox/$folder"
            params={{ folder: 'spam' }}
            icon={ArchiveX}
          >
            {t('Spam')}
          </MailboxSidebarLink>
          <MailboxSidebarLink
            to="/mailbox/$folder"
            params={{ folder: 'trash' }}
            icon={Trash2}
          >
            {t('Trash')}
          </MailboxSidebarLink>
        </nav>
      </aside>
      <div className="min-w-0 flex-1 overflow-hidden">
        <Outlet />
      </div>
    </>
  );
}

function MailboxLayoutPending() {
  return (
    <>
      <aside
        className="flex w-64 shrink-0 flex-col gap-3 border-r border-border bg-background px-3 py-5"
        aria-busy="true"
      >
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-10 w-full rounded-full" />
      </aside>
      <div className="min-w-0 flex-1 bg-background" />
    </>
  );
}
